import { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { Activity, Shield, LogOut, User, Layers, Radio, Map, Ship, Plane, Wifi, Settings, Building2, Book, CreditCard, Camera, Crosshair, Network, Globe, Code2, ScanSearch, Menu, X, Wallet, ShieldAlert } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { BackgroundJobsIndicator } from '@/components/BackgroundJobsIndicator'
import { ImpersonationBanner } from '@/components/ImpersonationBanner'

const navigation = [
  { name: 'Dashboard', href: '/app/dashboard', icon: Activity, moduleId: null }, // Always accessible
  { name: 'Monitor', href: '/app/monitor', icon: Globe, moduleId: null }, // Public-facing intelligence globe
  { name: 'X Investigation', href: '/app/x-investigation', icon: Layers, moduleId: 'x-investigation' },
  { name: 'Social Intelligence', href: '/app/social-stream', icon: Radio, moduleId: 'social-stream' },
  { name: 'BreachINT', href: '/app/breach', icon: Shield, moduleId: 'breachint' },
  { name: 'Profiler', href: '/app/profiler', icon: ScanSearch, moduleId: 'profiler' },
  { name: 'Wallet Intelligence', href: '/app/wallet-intel', icon: Wallet, moduleId: 'wallet-intel' },
  { name: 'Watchlist', href: '/app/watchlist', icon: ShieldAlert, moduleId: 'watchlist' },
  { name: 'GEOINT Wayback', href: '/app/geoint-wayback', icon: Map, moduleId: 'geoint-wayback' },
  { name: 'Maritime Intel', href: '/app/geoint-maritime', icon: Ship, moduleId: 'maritime' },
  { name: 'Air Monitoring', href: '/app/air-monitoring', icon: Plane, moduleId: 'air-monitoring' },
  { name: 'WiFi Intelligence', href: '/app/wifi-intelligence', icon: Wifi, moduleId: 'wifi-intelligence' },
  { name: 'Facial Recognition', href: '/app/facial-recognition', icon: Camera, moduleId: 'facial-recognition' },
  { name: 'Cyber Terrorism', href: '/app/cyber-terrorism', icon: Crosshair, moduleId: 'cyber-terrorism' },
  { name: 'Playground', href: '/app/playground', icon: Network, moduleId: 'playground' },
  { name: 'Organizations', href: '/app/organizations', icon: Building2, moduleId: null }, // Always accessible for creating/managing orgs
  { name: 'API Documentation', href: '/api-docs', icon: Book, moduleId: null }, // Always accessible
  { name: 'Developer Portal', href: '/app/developer', icon: Code2, moduleId: null }, // Always accessible
  { name: 'Admin Panel', href: '/app/admin', icon: Settings, moduleId: null, adminOnly: true }, // Admin only
]

export function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)

  // Hide sidebar on dashboard, show on all other pages
  // Check for exact match or trailing slash
  const isDashboard = location.pathname === '/app/dashboard' || location.pathname === '/app/dashboard/'
  // Monitor renders its own full-viewport UI (a `fixed inset-0` 3D globe with
  // its own overlay panels — see MonitorDashboard.tsx) built to be shown with
  // zero surrounding chrome, same as the public /monitor route. Nested inside
  // the sidebar+top-bar shell like every other module page, that chrome
  // visually overlapped/clipped it instead of the "full page" it's designed
  // to be — so this route gets the same full-bleed treatment as Dashboard,
  // plus the top bar too (Dashboard keeps the top bar for nav/profile access;
  // Monitor doesn't need it, it has its own controls).
  const isMonitor = location.pathname === '/app/monitor' || location.pathname === '/app/monitor/'
  const showSidebar = !isDashboard && !isMonitor
  const showTopBar = !isMonitor
  // The Air Intelligence Console fills everything under the top bar edge to edge.
  const isAirConsole = location.pathname.startsWith('/app/air-monitoring')

  // Filter navigation based on user's module access
  const hasModuleAccess = (moduleId: string | null): boolean => {
    if (!moduleId) return true // Always accessible (Dashboard, Admin Panel)
    if (user?.role === 'admin') return true // Admins have access to everything
    const userModules = user?.modules || []
    return userModules.includes(moduleId)
  }

  const isAdminOnly = (item: any): boolean => {
    return item.adminOnly === true && user?.role !== 'admin'
  }

  const filteredNavigation = navigation.filter(item => {
    // Hide admin-only items for non-admins
    if (isAdminOnly(item)) return false
    // Check module access
    return hasModuleAccess(item.moduleId)
  })

  return (
    <div className="min-h-screen bg-background dark">
      {/* Sidebar - hidden on dashboard */}
      {showSidebar && (
        <>
          {/* Mobile backdrop */}
          {mobileOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/60 md:hidden"
              onClick={() => setMobileOpen(false)}
            />
          )}
          <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
            <div className="flex h-16 items-center justify-between px-6 border-b border-border">
              <h1 className="text-xl font-bold text-primary">Sycek OSINT</h1>
              <button
                className="md:hidden text-muted-foreground hover:text-foreground"
                onClick={() => setMobileOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="mt-6 px-3 overflow-y-auto max-h-[calc(100vh-4rem)]" data-tour="navigation">
              <ul className="space-y-1">
                {filteredNavigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => setMobileOpen(false)}
                        className={`
                          flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                          ${isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                          }
                        `}
                      >
                        <item.icon className="mr-3 h-4 w-4" />
                        {item.name}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </nav>
          </div>
        </>
      )}

      {/* Main content */}
      <div className={`min-w-0 overflow-x-hidden ${showSidebar ? "md:pl-64" : ""}`}>
        <ImpersonationBanner />
        {/* Top bar */}
        {showTopBar && (
          <div className="sticky top-0 z-40 flex h-16 items-center justify-between bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border px-4 md:px-6">
            <div className="flex items-center space-x-3">
              {showSidebar && (
                <button
                  className="md:hidden text-muted-foreground hover:text-foreground"
                  onClick={() => setMobileOpen(v => !v)}
                >
                  <Menu className="h-5 w-5" />
                </button>
              )}
              <h2 className="text-lg font-semibold text-foreground">
                {filteredNavigation.find(item => item.href === location.pathname)?.name || 'Sycek OSINT'}
              </h2>
            </div>

            <div className="flex items-center space-x-2">
              <BackgroundJobsIndicator />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center space-x-2" data-tour="profile">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline max-w-[140px] truncate">{user?.full_name || user?.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/app/profile')}>
                    <User className="mr-2 h-4 w-4 inline" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/app/subscription')}>
                    <CreditCard className="mr-2 h-4 w-4 inline" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/api-docs')}>
                    <Book className="mr-2 h-4 w-4 inline" />
                    API Documentation
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="mr-2 h-4 w-4 inline" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className={`min-w-0 ${showTopBar && !isAirConsole ? 'p-6' : ''}`}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}